import React, { useState, useEffect } from 'react';
import { graphql, useStaticQuery, Link as GatsbyLink } from 'gatsby';
import FocusTrap from 'focus-trap-react';
import { VALUE_ESCAPE } from 'keycode-js';
import Link from '../link/link';
import SocialIcons from '../social-icons/social-icons';
import IconWithText from '../icon/icon-with-text';
import { DatoCmsHeader, NavigationBlock } from '../../schema/datocms/header';
import { DatoCmsHoursOfOperation, DatoCmsLink, DatoCmsContactInfo } from '../../schema/datocms/blocks';
import Icon from '../icon/icon';
import useKeypress from '../../hooks/useKeyPress';
import useViewport from '../../hooks/useViewport';
import { AppConfig } from '../../environment/app-config.model';
import ContactLink from '../contact-link/contact-link';
import { ButtonStyle, ButtonStyles } from '../../schema/enum/button-styles';
import Button from '../button/button';
import { ComponentTypeNames } from '../../schema';

const HeaderQuery = graphql`
    query HeaderQuery {
        environment: environmentJson {
            bhtpQuoteUrl
            accountPageUrl
            policiesPageUrl
            claimsPageUrl
            disbursementsPageUrl
        }
        header: datoCmsHeader {
            logo {
                url
                alt
            }
            primaryNavigation {
                __typename
                ...LinkFragment
                ...ContactInfoFragment
                ...SocialMediaBlockFragment
                ...HoursFragment
            }
            secondaryNavigation {
                __typename
                ...LinkFragment
                ...ContactInfoFragment
                ...SocialMediaBlockFragment
                ...HoursFragment
            }
        }
    }
`;

export interface HeaderQueryResult {
    environment: Partial<AppConfig>;
    header: DatoCmsHeader;
}

export interface HeaderProps {
    simple?: boolean | null;
    getQuoteOptions?: { showOnDesktop?: boolean, text?: string, style?: ButtonStyle };
    banner?: JSX.Element
}

const Header = ({ simple, getQuoteOptions, banner }: HeaderProps): React.ReactElement => {

    const { header }: HeaderQueryResult = useStaticQuery(HeaderQuery);

    // const { updateUser }: UserDataContext = useContext(UserContext);

    const [mobileMenuOpen, setMobileMenuOpen] = useState<boolean>(false);

    useKeypress(VALUE_ESCAPE, !simple && mobileMenuOpen, () => escapeHandler());

    const { viewportWidth } = useViewport();

    const quoteButton: DatoCmsLink = {
        __typename: ComponentTypeNames.LINK,
        displayText: getQuoteOptions?.text || 'Get A Quote',
        internalPageReference: {
            __typename: 'Page',
            slug: ''
        },
        internalSectionComponentId: 'quote_form',
        styleOverride: getQuoteOptions?.style || ButtonStyles.DEFAULT,
        additionalOnClickActions: () => setMobileMenuOpen(false)
    };

    useEffect(() => {
        if (viewportWidth >= 1024) {
            // Immediatly close nav when screen is resized to larger breakpoint
            setMobileMenuOpen(false);
        }
    }, [viewportWidth]);

    const onMenuButtonClick = (event: React.MouseEvent): void => {
        event.preventDefault();
        event.stopPropagation();

        const shouldShow: boolean = !mobileMenuOpen;
        setMobileMenuOpen(shouldShow);
        if (shouldShow) {
            setTimeout(() => document.addEventListener('click', hideMobileMenu), 0);
        } else {
            document.removeEventListener('click', hideMobileMenu);
        }
    };

    function hideMobileMenu(event: MouseEvent): void {
        const mobileMenu: HTMLElement | null = document.getElementById('mobile-menu');
        if (event.target && mobileMenu && !mobileMenu.contains(event.target as HTMLElement)) {
            setMobileMenuOpen(false);
        }
    }

    const escapeHandler = (): void => {
        setMobileMenuOpen(false);
    };

    const navigationMapper = (nav: NavigationBlock, index: number, className: string): React.ReactElement | null => {
        let navItem: React.ReactElement | null = null;
        const classList = 'font-sans font-bold uppercase text-primary hover:text-secondary whitespace-nowrap p-1';

        switch (nav.__typename) {
            case 'DatoCmsLink': {
                const link: DatoCmsLink = nav as DatoCmsLink;
                navItem = <Link internalPageReference={link.internalPageReference} internalPageSectionId={link.internalSectionComponentId} externalPageUrl={link.externalPageUrl} className={classList}>{link.displayText}</Link>;
            } break;
            case 'DatoCmsContactInfo': {
                const { iconName, contact } = nav as DatoCmsContactInfo;
                navItem = <ContactLink iconName={iconName} {...contact} className={classList} />;
            } break;
            case 'DatoCmsSocialMediaBlock':
                navItem = <SocialIcons />;
                break;
            case 'DatoCmsHoursOfOperation':
                const { iconName, displayText } = nav as DatoCmsHoursOfOperation;
                navItem = <IconWithText iconName={iconName} text={displayText} className={classList} />;
                break;
            default:
                console.warn(`Unknown Navigation element: ${nav.__typename}`);
                navItem = null;
                break;
        }

        return navItem !== null ?
            <li key={index} className={className}>{navItem}</li> :
            null;
    };

    const focustTrapOptions = {
        fallbackFocus: 'header'
    };
    return (
        <header className="relative">
            <div className="absolute opacity-0 -z-50 bg-secondary text-white w-full block text-center h-auto focus-within:opacity-100 focus-within:py-2 focus-within:z-50 focus-within:relative">
                <a href="#main-content" className="focus:py-1 focus:px-2 focus:outline-black">Skip to Content</a>
            </div>
            <FocusTrap active={mobileMenuOpen} focusTrapOptions={focustTrapOptions}>
                <div>
                    <div>
                        { banner ? <div className='relative z-30'>{banner}</div> : null }
                        <nav  role="navigation">
                            <div className={`relative bg-white z-30 w-full flex p-3 lg:p-6 ${ !simple ? 'justify-between' : 'justify-center' } lg:items-start items-center border-b-2 border-primary`}>
                                <GatsbyLink to="/" className={`w-3/5 lg:w-1/5 h-16 ml-3 lg:mx-6 lg:order-2 flex ${ !simple ? 'justify-start lg:justify-center' : 'justify-center' }`} title="Homepage">
                                    <img src={header.logo.url} alt={header.logo.alt} />
                                </GatsbyLink>

                                {
                                    !simple ?
                                        <>
                                            <ul className="hidden lg:flex flex-wrap justify-start items-center text-sm lg:mt-1 mt-0 xl:text-base w-2/5 order-1 xl:ml-10 lg:p-2 p-0" data-testid="desktop-primary-nav">
                                                {
                                                    header.primaryNavigation
                                                        .map((nav: NavigationBlock, index: number) => navigationMapper(nav, index, 'my-2 mx-2'))
                                                }
                                            </ul>

                                            <ul className="hidden lg:flex flex-wrap justify-end items-center text-sm xl:text-base w-2/5 order-3 xl:mr-10 lg:p-2 p-0" data-testid="desktop-secondary-nav">
                                                {
                                                    header.secondaryNavigation
                                                        .map((nav: NavigationBlock, index: number) => navigationMapper(nav, index, 'my-2 mx-2'))
                                                }

                                                { getQuoteOptions?.showOnDesktop && <Button className="btn-small" {...quoteButton} /> }
                                            </ul>

                                            <button onClick={(ev) => onMenuButtonClick(ev)} aria-expanded={mobileMenuOpen} aria-controls="mobile-menu" aria-label="Toggle Navigation" className="lg:hidden" data-testid="mobile-nav-button">
                                                <Icon name="close" className={`${mobileMenuOpen ? 'block' : 'hidden'} lg:hidden w-12 h-12 text-primary fill-current`}/>
                                                <Icon name="menu" className={`${ !mobileMenuOpen ? 'block' : 'hidden'} lg:hidden w-12 h-12 text-primary fill-current`}/>
                                            </button>
                                        </> : null
                                }

                            </div>

                            {

                                // mobile menu is rendered, but hidden so that nested components don't lose state each time the
                                // mobile menu is opened and closed
                                !simple ?
                                    <div
                                        id="mobile-menu"
                                        data-testid="mobile-menu"
                                        aria-hidden={!mobileMenuOpen}
                                        className={`mobile-menu ${mobileMenuOpen ? 'menu-open' : ''}`}>
                                        <div className='relative'>
                                            <div id='mobile-main-menu' className='overflow-x-hidden absolute right-0 flex flex-col justify-end w-full p-6 bg-white border-l-2 border-r-2 border-b-2 border-primary'>
                                                <ul className="flex flex-col w-full">
                                                    {
                                                        [...header.primaryNavigation, ...header.secondaryNavigation]
                                                            .map((nav: NavigationBlock, index: number) => navigationMapper(nav, index, 'my-2'))
                                                    }
                                                </ul>

                                                { <Button className='text-center' {...quoteButton} /> }
                                            </div>
                                        </div>
                                    </div> : null
                            }
                        </nav>
                    </div>

                    {
                        !simple ?
                            <div className={`${ mobileMenuOpen ? 'bg-gray z-10' : 'bg-transparent -z-50' } lg:hidden absolute top-full left-0 w-full h-screen bg-opacity-75 transition duration-700`} data-testid="overlay" /> : null
                    }
                </div>
            </FocusTrap>
        </header>
    );
};

export default Header;
